import { Card, CardActions, CardContent, Typography, useEventCallback } from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { DateTime } from 'luxon';
import { noop } from 'shell/utils/noop';
import { PmiButton } from 'shell/components/shared/button';
import Form from './form';
import { alphanumericRegex } from 'shell/constants/regex';

export interface AmountForm {
  code?: string;
  description?: string;
  amount?: number;
  dateFrom?: DateTime | null;
  dateTo?: DateTime | null;
  codeAgency?: string; // to request
}

const validationSchema = yup
  .object({
    code: yup
      .string()
      .nullable()
      .required('Campo obbligatorio')
      .matches(alphanumericRegex, 'Il campo non è valido'),
    description: yup.string().nullable().required('Campo obbligatorio'),
    amount: yup.number().nullable().required('Campo obbligatorio').min(0, 'Campo negativo'),
    dateFrom: yup.date().nullable().typeError('Data non valida'),
    dateTo: yup
      .date()
      .nullable()
      .typeError('Data non valida')
      .when('dateFrom', (dateFrom, formvalidationSchema) =>
        dateFrom && dateFrom?.toString() !== 'Invalid Date'
          ? formvalidationSchema.min(
              dateFrom,
              'La data deve essere maggiore o uguale della data Dal'
            )
          : formvalidationSchema.nullable()
      ),
  })
  .test('amount', 'Controllo', (values) => {
    const castedValues = values as unknown as AmountForm;

    if (!castedValues.amount) {
      return true;
    }
    const intPart = Number.parseInt(castedValues.amount?.toString());

    const decimalPart = Number.parseFloat(castedValues.amount?.toString())
      ?.toString()
      .split('.')[1];

    if (intPart?.toString()?.length > 6 || decimalPart?.toString()?.length > 2) {
      return new yup.ValidationError(
        'Formato non valido, 6 interi e 2 decimali',
        undefined,
        'amount'
      );
    }

    return true;
  });

interface FormAmountProps {
  onSubmit?: (form?: AmountForm) => void;
  onCancel: () => void;
  title: string;
  disabled?: boolean;
  isEdit?: boolean;
  initialValues?: AmountForm;
}

const FormAmount = ({
  onSubmit,
  onCancel,
  title,
  disabled = false,
  initialValues,
  isEdit,
}: FormAmountProps): React.ReactElement => {
  const amountForm = useFormik<AmountForm>({
    initialValues: initialValues || {
      code: undefined,
      description: undefined,
      amount: undefined,
      dateFrom: null,
      dateTo: null,
      codeAgency: undefined,
    },
    validationSchema: validationSchema,
    onSubmit: onSubmit || noop,
    enableReinitialize: true,
  });

  const onChange = useEventCallback(
    (key: keyof AmountForm, value: AmountForm[keyof AmountForm]) => {
      amountForm.setValues({
        ...(amountForm.values || {}),
        [key]: value,
      });
    }
  );

  return (
    <Card raised className="pmi-my-3">
      <form onSubmit={amountForm.handleSubmit}>
        <CardContent style={{ padding: '24px' }}>
          <Typography gutterBottom variant="h5" component="h2" color="secondary">
            {title}
          </Typography>
          <div style={{ padding: '24px 0 0 0' }}>
            <Form
              onChange={onChange}
              values={amountForm.values}
              errors={amountForm.errors}
              touched={amountForm.touched}
              disabled={disabled}
              isEdit={isEdit}
            />
          </div>
        </CardContent>
        <CardActions style={{ padding: '24px 24px 16px 24px' }}>
          <PmiButton variant="contained" color="primary" type="submit" disabled={disabled}>
            Conferma
          </PmiButton>
          <PmiButton
            variant="outlined"
            color="cdmBrand"
            onClick={() => {
              onCancel();
            }}
          >
            Annulla
          </PmiButton>
        </CardActions>
      </form>
    </Card>
  );
};

FormAmount.displayName = 'Form Amount component';

export default FormAmount;
